const NIF_LETTERS = 'TRWAGMYFPDXBNJZSQVHLCKE'

const CIF_TYPE_LETTERS = new Set(
  'ABCDEFGHJNPQRSUVW'.split('')
)

const CIF_CONTROL_LETTERS = 'JABCDEFGHI'

const CIF_NUMERIC_CONTROL_TYPES = new Set(['A', 'B', 'E', 'H'])

const CIF_LETTER_ONLY_CONTROL_TYPES = new Set(['N', 'P', 'Q', 'R', 'S', 'W'])

function spanishDomesticBodyForValidation(normalized: string): string | null {
  if (normalized.startsWith('ES') && normalized.length === 11) {
    return normalized.substring(2)
  }
  if (normalized.length === 9) {
    return normalized
  }
  return null
}

function isNifShape(body: string): boolean {
  return /^\d{8}[A-Z]$/.test(body)
}

function isNieShape(body: string): boolean {
  return /^[XYZ]\d{7}[A-Z]$/.test(body)
}

function isCifShape(body: string): boolean {
  const type = body.charAt(0)
  return CIF_TYPE_LETTERS.has(type) && /^\d{7}[0-9A-J]$/.test(body.substring(1))
}

function matchesSpanishDomesticShape(body: string): boolean {
  return isNifShape(body) || isNieShape(body) || isCifShape(body)
}

export function spanishDomesticBodyForComparison(normalized: string): string | null {
  if (!looksLikeSpanishTaxId(normalized)) {
    return null
  }
  return spanishDomesticBodyForValidation(normalized)
}

export function looksLikeSpanishTaxId(normalized: string): boolean {
  if (!normalized) {
    return false
  }
  const body = spanishDomesticBodyForValidation(normalized)
  return body !== null && body.length === 9 && matchesSpanishDomesticShape(body)
}

function isValidNifOrNie(body: string): boolean {
  let numericPart: string
  if (isNieShape(body)) {
    const prefix = body.charAt(0)
    const mapped = prefix === 'X' ? '0' : prefix === 'Y' ? '1' : '2'
    numericPart = mapped + body.substring(1, 8)
  } else {
    numericPart = body.substring(0, 8)
  }
  const number = Number.parseInt(numericPart, 10)
  const expectedLetter = NIF_LETTERS.charAt(number % 23)
  return body.charAt(8) === expectedLetter
}

function matchesCifControl(type: string, control: string, controlDigit: number): boolean {
  const expectedLetter = CIF_CONTROL_LETTERS.charAt(controlDigit)
  const expectedDigit = String(controlDigit)

  if (CIF_NUMERIC_CONTROL_TYPES.has(type)) {
    return control === expectedDigit
  }
  if (CIF_LETTER_ONLY_CONTROL_TYPES.has(type)) {
    return control === expectedLetter
  }
  return control === expectedDigit || control === expectedLetter
}

function isValidCif(body: string): boolean {
  const type = body.charAt(0)
  const digits = body.substring(1, 8)
  const control = body.charAt(8)

  let sum = 0
  for (let i = 0; i < digits.length; i++) {
    const digit = digits.charCodeAt(i) - 48
    if (i % 2 === 0) {
      const doubled = digit * 2
      sum += Math.floor(doubled / 10) + (doubled % 10)
    } else {
      sum += digit
    }
  }
  const controlDigit = (10 - (sum % 10)) % 10

  return matchesCifControl(type, control, controlDigit)
}

export function isValidSpanishTaxId(normalized: string): boolean {
  if (!looksLikeSpanishTaxId(normalized)) {
    return false
  }
  const body = spanishDomesticBodyForValidation(normalized)
  if (!body) {
    return false
  }
  if (isNifShape(body) || isNieShape(body)) {
    return isValidNifOrNie(body)
  }
  if (isCifShape(body)) {
    return isValidCif(body)
  }
  return false
}
